import React, {useEffect, useState} from 'react';

import AwesomeDebouncePromise from 'awesome-debounce-promise';
import Col from 'react-bootstrap/Col';
import {Dropdown, Row} from 'react-bootstrap';
import {Tooltip} from '@/components/tooltips';
import Button from '@/components/ui/Button';
import {Icon} from '@/components/icons/Icon';
import LoadingWrapper from '@/components/loading/LoadingWrapper';
import PaginationComponent from '@/components/pagination/PaginationComponent';
import Form from 'react-bootstrap/Form';
import useOptimizedConfig from '@/hooks/useOptimizedConfig';
import usePaginatedData from '@/utils/usePaginatedData';
import EvaluationsSettingsModal from './EvaluationsSettingsModal';
import HelpTextBlock from '@/components/help_text/HelpTextBlock';
import TableComponent from '@/components/table/Table';
import ConditionalNav from '@/components/navigation/ConditionalNav';
import PrioritiseButton from '@/components/utils/PrioritiseButton';
import i18n from 'i18n';
import {useFormattedDateTime} from '@/components/utils/FormattedDateTime';
import {hasPermission} from '@/utils/permissions/permissionDebug';
interface Evaluation {
    id: number;
    evaluationLabel: string;
    description: string;
    active: boolean;
    lastUpdated: string;
    createdOn: string;
    evaluationType: string;
    evaluationIdentifier: string;
    priority: number;
}

interface UserData {
    permissions?: string[];
}

interface PaginatedDataResult<T> {
    data: T[];
    loading: boolean;
    currentPage: number | null;
    setCurrentPage: (page: number) => void;
    pages: number[] | null;
}

const setSearchQueryState = async (
    setSearchQuery: (value: string) => void,
    value: string,
    setPage: (page: number) => void
): Promise<void> => {
    setPage(1);
    setSearchQuery(value);
};

const setSearchQueryStateDebounced = AwesomeDebouncePromise(setSearchQueryState, 350);

const EvaluationsListView: React.FC = React.memo(() => {
    const {formatDateTime, isLoaded: configLoaded} = useFormattedDateTime();
    const {userData, loading: loadingUserData} = useOptimizedConfig();
    const [settingsModalOpen, setSettingsModalOpen] = useState<boolean>(false);
    const [selectedEvaluationId, setSelectedEvaluationId] = useState<number>(0);
    const [sortedEvaluations, setSortedEvaluations] = useState<Evaluation[]>([]);
    const [searchQuery, setSearchQuery] = useState<string>('');

    const {
        data: evaluations,
        loading: loadingEvaluations,
        currentPage,
        setCurrentPage,
        pages,
    }: PaginatedDataResult<Evaluation> = usePaginatedData(window.URLS.evaluationsResource, {
        storeResolver: 'evaluations',
        requestParams: {
            params: {
                query: searchQuery,
            },
        },
    });

    useEffect(() => {
        setSortedEvaluations(evaluations.sort((a, b) => b.priority - a.priority));
    }, [evaluations]);

    const canCreateEvaluation = hasPermission('fs.evaluation.create', userData?.permissions, true);
    const canChangeEvaluation = hasPermission('fs.evaluation.update', userData?.permissions, true);
    const userCanAccessEvaluationsResource = hasPermission('fs.evaluation.list', userData?.permissions, true);

    if (!userCanAccessEvaluationsResource && !loadingUserData) {
        return (
            <>

                <ConditionalNav>

                </ConditionalNav>

                <HelpTextBlock helpText={i18n.t('ht_each_conversation.text')} />

                <div className="alert alert-danger" role="alert">
                    {i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                </div>
            </>
        );
    }

    const inputChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        setSearchQueryStateDebounced(setSearchQuery, event.target.value, setCurrentPage);
    };

    const openSettingsModal = (evaluationId: number): void => {
        setSelectedEvaluationId(evaluationId);
        setSettingsModalOpen(true);
    };

    const closeSettingsModal = (): void => {
        setSettingsModalOpen(false);
        setSelectedEvaluationId(0);
    };

    const active = currentPage || 1;
    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };


    const columns = [
        {
            header: i18n.t('th_evaluation.text'),
            accessorKey: 'evaluationLabel',
            cell: (cell: any) => {
                const isPriority = cell.row.original.priority;

                return (
                    <span>
                        {cell.getValue()}
                        {isPriority && (
                            <span style={{marginLeft: '0.5em'}}>
                                <PrioritiseButton isPriority={isPriority} />
                            </span>
                        )}
                    </span>
                );
            },
        },
        {
            header: i18n.t('th_description.text'),
            accessorKey: 'description',
            cell: (cell: any) => {
                if (!cell.getValue() || cell.getValue() === '-') {
                    return '-';
                } else if (cell.getValue().length > 100) {
                    const plainString = cell.getValue().replace(/<[^>]+>/g, '');

                    return (
                        <span title={plainString}>
                            {`${plainString.substring(0, 100)}...`}
                        </span>
                    );
                } else {
                    const plainString = cell.getValue().replace(/<[^>]+>/g, '');

                    return (
                        <span title={plainString}>
                            {plainString}
                        </span>
                    );
                }
            },
        },
        {
            header: i18n.t('th_active.text'),
            accessorKey: 'active',
            cell: (cell: any) => {
                if (cell.getValue() === true) {
                    return (<Icon name="mdi-check" className="text-success" />);
                } else {
                    return (<Icon name="mdi-close" className="text-danger" />);
                }
            },
        },
        {
            id: 'i18ntthlastmodified8text',
            header: i18n.t('th_last_modified.text'),
            accessorFn: (cell: Evaluation) => {
                if (!cell.lastUpdated || cell.lastUpdated === '-') {
                    return configLoaded ? formatDateTime(cell.createdOn, true, {setZone: false},
                    ) : '';
                }

                return configLoaded ? formatDateTime(cell.lastUpdated, true, {setZone: false},
                ) : '';
            },
        },
        {
            header: i18n.t('th_action.text'),
            cell: (cell: any) => (
                <Tooltip content={i18n.t('jt_actions.text')} placement="top">
                    <Dropdown className="d-inline-block">
                        <Dropdown.Toggle
                            as={Button}
                            variant="outline-info"
                            size="sm"
                            icon="mdi-dots-horizontal"
                            className="dropdown-toggle-no-caret"
                            id="settings-dropdown-1"
                            data-cy="eval-settings-toggle"
                        />

                        <Dropdown.Menu align="end">
                            {
                                canChangeEvaluation ? (
                                    <Dropdown.Item
                                        onClick={() => openSettingsModal(cell.row.original.id)}
                                        data-cy="eval-settings-btn"
                                    >
                                        <Icon name="mdi-settings" className="mr-2" /> {i18n.t('jt_settings.text')}
                                    </Dropdown.Item>
                                ) : (
                                    <Tooltip
                                        content={i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                                        placement="top"
                                    >
                                        <div
                                            className="ml-auto"
                                            style={{
                                                cursor: 'not-allowed',
                                                display: 'flex',
                                            }}
                                        >
                                            <Dropdown.Item
                                                style={{pointerEvents: 'none'}}
                                                disabled
                                                data-cy="evaluations-list-btn">
                                                <Icon name="mdi-settings" className="mr-2" /> {i18n.t('jt_settings.text')}
                                            </Dropdown.Item>
                                        </div>
                                    </Tooltip>
                                )
                            }
                        </Dropdown.Menu>
                    </Dropdown>
                </Tooltip>
            ),
        },
    ];

    if (loadingUserData) {
        return (
            <LoadingWrapper loading={true} loadingText={i18n.t('jt_loading_data_general.text')} minHeight="400px"><div /></LoadingWrapper>
        );
    } else {
        return (
            <>
                <ConditionalNav />

                <HelpTextBlock helpText={i18n.t('ht_each_conversation.text')} />

                <Row>
                    <Col lg={3}>
                        <Form
                            className="mb-2 search-box"
                            onSubmit={(e: React.FormEvent) => {
                                e.preventDefault();
                            }}
                        >
                            <Form.Control
                                type="text"
                                placeholder={i18n.t('es_search.text')}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => inputChange(event)}
                                data-cy="eval-search-field"
                            />
                        </Form>
                    </Col>
                    <Col lg={9} className="d-flex justify-content-end">
                        {
                            canCreateEvaluation ? (
                                <Button
                                    variant="info"
                                    className='mb-2'
                                    size="sm"
                                    icon="mdi-plus"
                                    onClick={() => setSettingsModalOpen(true)}
                                    data-cy="new-evaluation-btn"
                                >
                                    {i18n.t('jt_add_new_evaluation.text')}
                                </Button>
                            ) : (
                                <Tooltip
                                    content={i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                                    placement="top"
                                >
                                    <div style={{cursor: 'not-allowed', display: 'flex'}}>
                                        <Button
                                            variant="secondary"
                                            size="sm"
                                            className='mb-2'
                                            icon="mdi-plus"
                                            disabled
                                            style={{pointerEvents: 'none'}}
                                            data-cy="evaluations-list-plus-btn">
                                            {i18n.t('jt_add_new_evaluation.text')}
                                        </Button>
                                    </div>
                                </Tooltip>
                            )
                        }
                    </Col>
                </Row>

                <LoadingWrapper
                    loading={loadingEvaluations}
                    loadingText={i18n.t('jt_loading_data_general.text')}
                    minHeight="300px"
                >
                    <div className="card">
                        <div className="card-body">
                                <TableComponent data={sortedEvaluations} columns={columns} />
                                <PaginationComponent
                                    pages={pages}
                                    currentPage={currentPage}
                                    onPageChange={handlePageChange}
                                />
                        </div>
                    </div>
                    {
                        settingsModalOpen ? (
                            <EvaluationsSettingsModal
                                modalOpen={settingsModalOpen}
                                closeModal={closeSettingsModal}
                                activeEvaluationId={selectedEvaluationId}
                            />
                        ) : null
                    }
                </LoadingWrapper>
            </>
        );
    }
});

export default EvaluationsListView; 